import { useEffect, useState } from "react";
import { api, getApiErrorMessage } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { currency, countryFlag, PIPELINE_STAGES, PHASE_COLORS } from "@/lib/format";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, RadialBarChart, RadialBar,
} from "recharts";
import { toast } from "sonner";

const COLORS = ["#2A4D3E", "#D97757", "#B26B00", "#2980B9", "#7A5AC2", "#8E9992", "#5C6660", "#C0392B"];

export default function Analytics() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/analytics/admin"); setD(data); }
      catch (e) { toast.error(getApiErrorMessage(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="text-sm text-[#5C6660]">Loading analytics…</div>;
  if (!d) return null;

  const stageData = d.stage_funnel.map((s) => {
    const stage = PIPELINE_STAGES.find((x) => x.id === s.stage);
    return { ...s, label: stage?.label || s.stage, phase: stage?.phase || "Other", value: s.value };
  });

  return (
    <div data-testid="analytics-page">
      <PageHeader
        eyebrow="Insights"
        title="Analytics"
        subtitle="Deep dive into pipeline performance, partner contribution and hospital throughput."
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card title="Pipeline value by stage" subtitle="Estimated value flowing through each stage">
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={stageData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" />
              <XAxis dataKey="label" stroke="#8E9992" fontSize={9} angle={-30} textAnchor="end" height={80} interval={0} />
              <YAxis stroke="#8E9992" fontSize={11} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E8E6E1", fontSize: 12 }} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {stageData.map((s, i) => <Cell key={i} fill={PHASE_COLORS[s.phase] || "#5C6660"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Quotation status" subtitle="Distribution of draft / sent / accepted / rejected">
          <ResponsiveContainer width="100%" height={320}>
            <PieChart>
              <Pie data={Object.entries(d.quotation_stats).map(([name, value]) => ({ name, value }))} dataKey="value" nameKey="name" innerRadius={60} outerRadius={110} paddingAngle={2}>
                {Object.keys(d.quotation_stats).map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E8E6E1", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12, textTransform: "capitalize" }} />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <Card title="Hospital throughput" subtitle="Patient volume vs revenue contribution">
        <div className="overflow-hidden rounded-xl border border-[#E8E6E1] mt-4">
          <table className="w-full">
            <thead className="bg-[#FDFCFB] border-b border-[#E8E6E1] text-xs font-semibold uppercase tracking-wider text-[#5C6660] text-left">
              <tr>
                <th className="px-5 py-3">Hospital</th>
                <th className="px-5 py-3">City</th>
                <th className="px-5 py-3">Patients</th>
                <th className="px-5 py-3">Revenue</th>
                <th className="px-5 py-3">Share</th>
              </tr>
            </thead>
            <tbody>
              {d.hospital_kpis.map((h) => {
                const totalRev = d.hospital_kpis.reduce((a, b) => a + b.revenue, 0) || 1;
                const share = (h.revenue / totalRev) * 100;
                return (
                  <tr key={h.hospital_id} className="border-b border-[#E8E6E1] last:border-0">
                    <td className="px-5 py-3 text-sm font-medium text-[#2A332C]">{h.name}</td>
                    <td className="px-5 py-3 text-sm text-[#5C6660]">{h.city}</td>
                    <td className="px-5 py-3 text-sm text-[#5C6660]">{h.patients}</td>
                    <td className="px-5 py-3 text-sm font-semibold text-[#2A332C]">{currency(h.revenue)}</td>
                    <td className="px-5 py-3 w-48">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-[#F2F4F3] rounded-full overflow-hidden">
                          <div className="h-full bg-[#2A4D3E]" style={{ width: `${share}%` }} />
                        </div>
                        <span className="text-[11px] text-[#8E9992] w-10 text-right">{share.toFixed(0)}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="mt-6">
        <Card title="Top referral partners" subtitle="Revenue contribution from each partner">
          <ResponsiveContainer width="100%" height={Math.max(280, d.top_partners.length * 50)}>
            <BarChart data={d.top_partners} layout="vertical" margin={{ left: 16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" />
              <XAxis type="number" stroke="#8E9992" fontSize={11} />
              <YAxis type="category" dataKey="name" stroke="#5C6660" fontSize={11} width={140} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E8E6E1", fontSize: 12 }} />
              <Bar dataKey="revenue" name="Revenue" fill="#2A4D3E" radius={[0, 6, 6, 0]} />
              <Bar dataKey="commission" name="Commission" fill="#D97757" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
}

function Card({ title, subtitle, children }) {
  return (
    <div className="bg-white border border-[#E8E6E1] rounded-2xl p-6">
      <div className="font-heading font-semibold text-lg text-[#2A332C]">{title}</div>
      {subtitle && <div className="text-xs text-[#8E9992] mt-0.5 mb-2">{subtitle}</div>}
      {children}
    </div>
  );
}
